import { SceneNodeSchema, SceneOpSchema } from "@uzverse/world-scene";
import type { SceneNode, SceneOp } from "@uzverse/world-scene";

/**
 * The client's copy of the public scene: nodes by id, applied idempotently by `version`
 * (docs/PHASE2_PLAN.md section 2.3). Pure and synchronous, so it is unit tested without React.
 * Every node and op is validated with the world-scene schemas; invalid ones are counted and
 * dropped (a bad node must never take the viewer down).
 */
export interface SceneState {
  nodes: Map<string, SceneNode>;
  /** Removed ids with the removal's version, so a late older upsert cannot bring a node back. */
  tombstones: Map<string, number>;
  dropped: number;
}

export const MAX_TOMBSTONES = 2000;

export function emptyScene(): SceneState {
  return { nodes: new Map(), tombstones: new Map(), dropped: 0 };
}

/** What one `applyOps` call changed, for highlights and for the store to know what to notify. */
export interface SceneChange {
  upserted: { node: SceneNode; previous: SceneNode | undefined; cause?: SceneOpCause }[];
  removed: string[];
}
export type SceneOpCause = NonNullable<Extract<SceneOp, { op: "upsert" }>["cause"]>;

/** Adds (or refreshes) a snapshot node; true when it was taken. */
export function putNode(state: SceneState, raw: unknown): boolean {
  const parsed = SceneNodeSchema.safeParse(raw);
  if (!parsed.success) {
    state.dropped += 1;
    return false;
  }
  const node = parsed.data;
  const have = state.nodes.get(node.id);
  if (have && have.version >= node.version) return false;
  state.nodes.set(node.id, node);
  state.tombstones.delete(node.id);
  return true;
}

/** Applies ops in order; stale versions are ignored. */
export function applyOps(state: SceneState, rawOps: readonly unknown[]): SceneChange {
  const change: SceneChange = { upserted: [], removed: [] };
  for (const raw of rawOps) {
    const parsed = SceneOpSchema.safeParse(raw);
    if (!parsed.success) {
      state.dropped += 1;
      continue;
    }
    const op = parsed.data;
    if (op.op === "upsert") {
      const have = state.nodes.get(op.node.id);
      if (have && have.version >= op.node.version) continue;
      const tomb = state.tombstones.get(op.node.id);
      if (tomb !== undefined && tomb >= op.node.version) continue;
      state.nodes.set(op.node.id, op.node);
      state.tombstones.delete(op.node.id);
      change.upserted.push({
        node: op.node,
        previous: have,
        ...(op.cause ? { cause: op.cause } : {}),
      });
    } else {
      const have = state.nodes.get(op.id);
      if (have && have.version > op.version) continue;
      const tomb = state.tombstones.get(op.id);
      if (!have && tomb !== undefined && tomb >= op.version) continue;
      state.nodes.delete(op.id);
      state.tombstones.set(op.id, op.version);
      if (state.tombstones.size > MAX_TOMBSTONES) {
        const oldest = state.tombstones.keys().next().value;
        if (oldest !== undefined) state.tombstones.delete(oldest);
      }
      if (have) change.removed.push(op.id);
    }
  }
  return change;
}

/** Part ids whose material differs between two versions of a node (for the change highlight). */
export function changedParts(previous: SceneNode | undefined, next: SceneNode): string[] {
  const out: string[] = [];
  const before = previous?.materials ?? {};
  for (const part of new Set([...Object.keys(before), ...Object.keys(next.materials)])) {
    if (JSON.stringify(before[part]) !== JSON.stringify(next.materials[part])) out.push(part);
  }
  return out;
}
